import { useEffect, useRef } from 'react';
import { useProgress } from '@/lib/store';
import { usePlayer } from '@/features/player/audioEngine';

const IDLE_MS = 2 * 60_000;

/* Counts time spent actually reading: the reader is on screen, the tab is
   visible, and the user has interacted (scroll/touch/key) in the last two
   minutes. Time while the player is running is NOT counted here — the audio
   engine logs it as listen/recite, so nothing is double-counted. */
export function useReadingTimer(pages: number[], surah?: number) {
  const acc = useRef(0);
  const last = useRef(Date.now());
  const lastInput = useRef(Date.now());
  const seen = useRef(new Set<number>());

  useEffect(() => { pages.forEach((p) => seen.current.add(p)); }, [pages]);

  useEffect(() => {
    const bump = () => { lastInput.current = Date.now(); };
    const evs = ['scroll', 'touchstart', 'pointerdown', 'keydown', 'wheel'] as const;
    // capture: scroll events don't bubble, and the page scrolls inside the app scroller
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true, capture: true }));

    const iv = setInterval(() => {
      const now = Date.now();
      const playing = usePlayer.getState().status === 'playing' || usePlayer.getState().status === 'gap';
      if (!document.hidden && !playing && now - lastInput.current < IDLE_MS) acc.current += (now - last.current) / 1000;
      last.current = now;
    }, 5000);

    const flush = () => {
      if (acc.current >= 15) {
        useProgress.getState().addSession({ kind: 'read', start: Date.now() - acc.current * 1000, seconds: Math.round(acc.current), pages: [...seen.current], surah });
      }
      acc.current = 0;
      seen.current = new Set();
    };
    const onHide = () => { if (document.hidden) flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, bump, { capture: true }));
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [surah]);
}
